import React, { useState } from 'react';
import { FiCheckCircle, FiAlertCircle, FiXCircle, FiMaximize, FiSearch, FiRefreshCw, FiShield } from 'react-icons/fi';
import axios from 'axios';

const CheckInScanner = () => {
    const [ticketInput, setTicketInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [stats, setStats] = useState({ totalCheckedIn: 42, totalRegistrations: 200 });

    const handleScan = async (codeToVerify) => {
        const code = codeToVerify || ticketInput;
        if (!code) return;

        setLoading(true);
        setScanResult(null);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(
                'https://api.tapcoe.com/api/v1/checkin/scan',
                { qrCodeData: code, gate: 'Main Entrance' },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const data = res.data;
            setScanResult(data);
            if (data.success) {
                setStats(prev => ({ ...prev, totalCheckedIn: prev.totalCheckedIn + 1 }));
            }
        } catch (err) {
            const errorData = err.response?.data || {
                success: false,
                result: 'INVALID',
                message: 'Invalid ticket payload or network error.'
            };
            setScanResult(errorData);
        } finally {
            setLoading(false);
            setTicketInput('');
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6 text-left pb-20">
            <div className="flex items-center justify-between bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
                <div>
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                        <FiMaximize className="text-purple-700" /> Organizer QR Check-in System
                    </h1>
                    <p className="text-xs font-medium text-slate-500 mt-1">Scan attendee QR pass or enter ticket ID to validate entry</p>
                </div>
                <div className="px-4 py-2.5 rounded-xl bg-purple-50 border border-purple-200 text-right shadow-xs">
                    <span className="text-[10px] text-slate-500 font-extrabold uppercase tracking-wider block">Checked In</span>
                    <span className="text-base font-black text-emerald-700">{stats.totalCheckedIn} / {stats.totalRegistrations}</span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Scanner Input Panel */}
                <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-sm">
                    <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        <FiSearch className="text-purple-700" /> Enter / Scan Ticket ID
                    </h2>

                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={ticketInput}
                            onChange={(e) => setTicketInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                            placeholder="e.g. TIX-89342719 or scan QR..."
                            className="flex-1 bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-mono font-bold"
                        />
                        <button
                            onClick={() => handleScan()}
                            disabled={loading}
                            className="px-5 py-3 rounded-xl bg-purple-700 text-white font-bold text-xs hover:bg-purple-800 transition-all shrink-0 shadow-sm"
                        >
                            {loading ? <FiRefreshCw className="animate-spin" /> : 'Validate'}
                        </button>
                    </div>

                    <div className="p-8 border-2 border-dashed border-purple-200 rounded-2xl text-center bg-slate-50 space-y-3">
                        <FiMaximize className="mx-auto w-12 h-12 text-purple-700 animate-pulse" />
                        <p className="text-xs font-extrabold text-slate-900">Point USB / Bluetooth Camera Scanner Here</p>
                        <span className="text-[10px] bg-slate-200 text-slate-700 font-bold px-3 py-1 rounded-full inline-block">Gate: Main Entrance</span>
                    </div>
                </div>

                {/* Scan Result Feedback Card */}
                <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-4 shadow-sm">
                    <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        <FiShield className="text-purple-700" /> Ticket Verification Result
                    </h2>

                    {scanResult ? (
                        <div className={`p-5 rounded-2xl border space-y-3 ${scanResult.result === 'VALID'
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                                : scanResult.result === 'ALREADY_USED'
                                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                                    : 'bg-rose-50 border-rose-200 text-rose-900'
                            }`}>
                            <div className="flex items-center gap-3">
                                {scanResult.result === 'VALID' ? (
                                    <FiCheckCircle className="w-8 h-8 text-emerald-600 shrink-0" />
                                ) : scanResult.result === 'ALREADY_USED' ? (
                                    <FiAlertCircle className="w-8 h-8 text-amber-600 shrink-0" />
                                ) : (
                                    <FiXCircle className="w-8 h-8 text-rose-600 shrink-0" />
                                )}
                                <div>
                                    <h3 className={`text-base font-black ${scanResult.result === 'VALID' ? 'text-emerald-800' : scanResult.result === 'ALREADY_USED' ? 'text-amber-800' : 'text-rose-800'
                                        }`}>
                                        {scanResult.result === 'VALID' ? 'TICKET VALID - ENTRY ALLOWED' : scanResult.result === 'ALREADY_USED' ? 'DUPLICATE CHECK-IN ATTEMPT' : 'INVALID TICKET'}
                                    </h3>
                                    <p className="text-xs font-medium text-slate-700 mt-0.5">{scanResult.message}</p>
                                </div>
                            </div>

                            {scanResult.ticket && (
                                <div className="pt-3 border-t border-slate-200 grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                        <span className="text-[10px] text-slate-500 font-bold block">Attendee</span>
                                        <span className="font-bold text-slate-900">{scanResult.ticket.userId?.name || 'Attendee Guest'}</span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] text-slate-500 font-bold block">Ticket Tier</span>
                                        <span className="font-bold text-purple-900">{scanResult.ticket.ticketTypeName || scanResult.ticket.ticketTypeId?.name || 'General Admission'}</span>
                                    </div>
                                    {(scanResult.ticket.selectedDate || scanResult.ticket.bookingId?.selectedDate) && (
                                        <div className="col-span-2 pt-2 border-t border-slate-200 flex flex-wrap items-center justify-between text-xs font-bold text-amber-800">
                                            <span>📅 Booked Date: {scanResult.ticket.selectedDate || scanResult.ticket.bookingId?.selectedDate}</span>
                                            {(scanResult.ticket.selectedTimeSlot || scanResult.ticket.bookingId?.selectedTimeSlot) && (
                                                <span>⏰ Slot: {scanResult.ticket.selectedTimeSlot || scanResult.ticket.bookingId?.selectedTimeSlot}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="p-10 text-center text-slate-500 text-xs font-bold border border-slate-200 rounded-2xl bg-slate-50">
                            Ready to scan tickets. Verification results will display here instantly.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CheckInScanner;
